import { TextArea } from '@radix-ui/themes';
import { FormHint } from 'components/form/hint';
import { FormInputWrapper } from 'components/form/input-wrapper';
import { FormLabel } from 'components/form/label';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { InputErrorType } from 'enums/form';
import { FormInputDef } from 'interfaces/form/input';
import {
  extendedErrorDetection,
  FormInputChangeDef,
} from 'interfaces/form/input-change';
import { FormInputWrapperDef } from 'interfaces/form/input-wrapper';
import { useContext, useEffect, useMemo, useState } from 'react';

export const FormTextArea = (
  props: FormInputDef &
    FormInputWrapperDef &
    FormInputChangeDef & {
      defaultValue?: string;
    }
) => {
  const { lastSubmitted, addError, removeError } = useContext(FormContext);

  const [touched, setTouched] = useState(!props.defaultTouched);

  const [value, setValue] = useState<string>(props.defaultValue ?? '');

  const errorType = useMemo(() => {
    if (props.required && !value.trim()) {
      return InputErrorType.Required;
    }

    return extendedErrorDetection(value, props) ?? InputErrorType.None;
  }, [value]);

  useEffect(() => {
    if (!props.label) {
      return;
    }

    (errorType === InputErrorType.None ? removeError : addError)(props.label);
  }, [props.label, errorType]);

  return (
    <EB>
      <FormInputWrapper {...props}>
        <FormLabel {...props} />

        <TextArea
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v);
            setTouched(true);

            props.onChange?.(v.trim());
            props.onOptionalChange?.(v.trim() || undefined);
          }}
        />

        <FormHint
          hint={props.hint}
          error={errorType}
          touched={touched || !!lastSubmitted}
        />
      </FormInputWrapper>
    </EB>
  );
};
